import React, { useRef, useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import Newsletter from "@/components/Newsletter";
import GooglePlayBadge from "@/components/GooglePlayBadge";
import { PRODUCTS } from "@/data/content";
import { useWaitlist } from "@/context/WaitlistContext";

const ACCENT = { coral: "#F28C82", mint: "#B8E3D9", gold: "#F5C86B" };

export default function ProductDetail() {
  const { slug } = useParams();
  const { openWaitlist } = useWaitlist();

  // Horizontal screenshot gallery scroll controls.
  const scrollRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 2);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 2);
  };
  const scrollByDir = (dir) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  };
  useEffect(() => {
    updateArrows();
    const onResize = () => updateArrows();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [slug]);

  const product = PRODUCTS.find((p) => p.slug === slug);
  if (!product) return <Navigate to="/products" replace />;
  const color = ACCENT[product.accent];

  return (
    <div data-testid="product-detail-page" className="pt-32">
      <section className="relative overflow-hidden py-12 md:py-20">
        <div className="mesh-bg">
          <div className="mesh-blob" style={{ width: 460, height: 460, background: color, top: -100, right: -40 }} />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12">
          <Link to="/products" className="inline-flex items-center gap-1.5 text-sm text-[#4B2E5A]/60 hover:text-[#4B2E5A]">
            <ArrowLeft className="h-4 w-4" /> All products
          </Link>
          <div className="mt-8 grid items-center gap-12 lg:grid-cols-2">
            <Reveal>
              <div className="flex items-center gap-3">
                {product.iconImage ? (
                  <img src={product.iconImage} alt={`${product.name} app icon`} className="h-14 w-14 rounded-2xl object-cover" />
                ) : (
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl" style={{ background: `${color}22` }}>
                    <product.icon className="h-7 w-7" strokeWidth={1.5} style={{ color }} />
                  </div>
                )}
                <span className="rounded-full bg-white/80 px-3 py-1 text-xs font-medium uppercase tracking-wider text-[#4B2E5A]">{product.available ? "Available Now" : "Coming Soon"}</span>
              </div>
              <h1 className="mt-6 font-heading text-4xl font-light tracking-tighter text-[#4B2E5A] md:text-6xl">{product.name}</h1>
              <p className="mt-5 text-lg leading-relaxed text-[#4B2E5A]/70">{product.description}</p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                {product.available ? (
                  <GooglePlayBadge height={52} url={product.appUrl} label={product.name} />
                ) : (
                  <button data-testid="detail-waitlist-btn" onClick={() => openWaitlist(product.name)} className="inline-flex items-center gap-2 rounded-full bg-[#4B2E5A] px-8 py-4 text-sm font-medium text-[#FCFAF7] hover:bg-[#4B2E5A]/90">
                    Join Waitlist <ArrowRight className="h-4 w-4" />
                  </button>
                )}
                <Link to="/contact" className="rounded-full border border-[#4B2E5A]/10 bg-white/80 px-8 py-4 text-sm font-medium text-[#4B2E5A] hover:bg-white">
                  Talk to us
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="overflow-hidden rounded-[24px] shadow-[0_20px_60px_rgba(75,46,90,0.15)]">
                <img src={product.image} alt={product.name} className="h-80 w-full object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {product.screenshots?.length > 0 && (
        <section data-testid="product-screenshots" className="pb-16 md:pb-24">
          <div className="mx-auto max-w-7xl">
            <Reveal className="px-6 md:px-12">
              <h2 className="text-center font-heading text-2xl font-medium text-[#4B2E5A] md:text-3xl">
                A peek inside {product.name}
              </h2>
              <p className="mx-auto mt-3 max-w-4xl text-center text-[#4B2E5A]/60">
                {product.screenshotsCaption || `Swipe through the ${product.name} app.`}
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="relative mt-10">
                <button
                  type="button"
                  onClick={() => scrollByDir(-1)}
                  aria-label="Scroll left"
                  className={`absolute left-3 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-[#4B2E5A]/10 bg-white/90 text-[#4B2E5A] shadow-lg backdrop-blur transition-all hover:bg-white ${atStart ? "pointer-events-none opacity-0" : "opacity-100"}`}
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <div
                  ref={scrollRef}
                  onScroll={updateArrows}
                  className="snap-x snap-mandatory overflow-x-auto pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                  <div className="mx-auto flex w-max gap-6 px-6 md:gap-8 md:px-12">
                    {product.screenshots.map((src, i) => (
                      <img
                        key={src}
                        src={src}
                        alt={`${product.name} app screen ${i + 1}`}
                        loading="lazy"
                        onLoad={updateArrows}
                        className="h-[460px] w-auto shrink-0 snap-center rounded-[28px] shadow-[0_20px_50px_rgba(75,46,90,0.15)] md:h-[560px]"
                      />
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => scrollByDir(1)}
                  aria-label="Scroll right"
                  className={`absolute right-3 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-[#4B2E5A]/10 bg-white/90 text-[#4B2E5A] shadow-lg backdrop-blur transition-all hover:bg-white ${atEnd ? "pointer-events-none opacity-0" : "opacity-100"}`}
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <section className="pb-16 md:pb-24">
        <div className="mx-auto max-w-5xl px-6 md:px-12">
          <Reveal>
            <h2 className="font-heading text-2xl font-medium text-[#4B2E5A] md:text-3xl">What {product.name} does</h2>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2">
            {product.features.map((f, i) => (
              <Reveal key={f} delay={i * 0.06}>
                <div className="glass flex items-start gap-4 rounded-[24px] p-6">
                  <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0" style={{ color }} />
                  <span className="text-lg text-[#4B2E5A]/80">{f}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <Newsletter />
    </div>
  );
}
